import React, { useCallback, useContext, useEffect, useMemo, useState, useRef } from 'react';
import Button, { buttonType } from '../../components/Button';
import OuterLayer from '../../containers/OuterLayer';
import { useLabels } from '../../hooks';
import { PageContext, OutersContainerContext, DeviceContext, UserContext, UserType } from '../../contexts';
import styles from './Nakdan.module.scss';
import Icon from '../../components/Icon';
import Alert from '../../components/Alert';
import { alertType } from '../../components/Alert/alertType';
import Loader from '../../components/Loader';
import WordVocalization from './wordVocalization';
import { useNakdanText } from '../../api/useNakdanText';
import { useNakdanRestoreKtivMale } from '../../api/useNakdanRestoreKtivMale';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { normalizeTextForSearch, saveReturnTo } from '../../utils/helpers';


const pageKey = "nakdan";

const Nakdan = () => {

    const { setPageKey } = useContext(PageContext);
    const { isMobileView } = useContext(DeviceContext);
    const { hasPermissions, userType } = useContext(UserContext)
    const {
        appLayoutTitleContainer,
        appLayoutSubTitleContainer
    } = useContext(OutersContainerContext);
    const { getLabel, getLocalUrlByLanguage } = useLabels();
    const alertRef = useRef();
    const alertWvRef = useRef();
    const containerRef = useRef();
    const userInputRef = useRef();
    const [userInput, setuserInput] = useState("");
    const [dataText, setdataText] = useState(null);
    const [originalInputText, setOriginalInputText] = useState("");
    const [lastSubmittedInput, setLastSubmittedInput] = useState("");
    const [showLoader, setShowLoader] = useState(false);
    const [fontSize, setFontSize] = useState(isMobileView ? 1.2 : 1.4);
    const [isLocked, setIsLocked] = useState(false);
    const [selectedWord, setSelectedWord] = useState("");
    const [selectedBaseWord, setSelectedBaseWord] = useState("");
    const [wordPosition, setWordPosition] = useState(null);
    const [textRemove, setTextRemove] = useState(false);
    const { loader: loadNakdanText, error: errorNakdanText, data: dataNakdanText } = useNakdanText(userInput);
    // Passing `null` prevents the hook from POSTing on every text edit.
    const { loader: loaduseNakdanRestoreKtivMale, error: erroruseNakdanRestoreKtivMale, data: dataNakdanRestoreKtivMale } =
        useNakdanRestoreKtivMale(textRemove ? dataText : null);
    const navigate = useNavigate();
    const location = useLocation();
    const [touchStartTime, setTouchStartTime] = useState(0);
    const [touchStartPos, setTouchStartPos] = useState({ x: 0, y: 0 });
    const [isTouchMoved, setIsTouchMoved] = useState(false);
    const [lastTapTime, setLastTapTime] = useState(0);

    const LONG_PRESS_DURATION = 500;
    const DOUBLE_TAP_THRESHOLD = 300;

    useEffect(() => {
        setPageKey(pageKey);
    }, [setPageKey]);

    useEffect(() => {
        if (!hasPermissions) {
            if (userType === UserType.guest) {
                saveReturnTo(location.pathname + location.search);
            }
            navigate(getLocalUrlByLanguage(`/`));
        }
    }, [hasPermissions, userType, getLocalUrlByLanguage, navigate, location.pathname, location.search]);

    const increaseFontSize = useCallback(() => {
        setFontSize(prevFontSize => prevFontSize + 0.2);
    }, []);

    const decreaseFontSize = useCallback(() => {
        setFontSize(prevFontSize => Math.max(prevFontSize - 0.2, 0.7));
    }, []);

    const clearText = useCallback(() => {
        setdataText(null);
        setOriginalInputText("");
        setLastSubmittedInput("");
        setTextRemove(false);
        if (userInputRef.current) {
            userInputRef.current.value = null;
            userInputRef.current.focus();
            userInputRef.current.readOnly = false;
        }
        setuserInput("");
        setSelectedWord("");
        setSelectedBaseWord("");
        setWordPosition(null);

    }, []);

    const closeAlert = useCallback(() => {
        if (alertRef.current) {
            alertRef.current.setShow(false);
        }
    }, [alertRef]);

    const closeVwAlert = useCallback(() => {
        if (alertWvRef.current) {
            alertWvRef.current.setShow(false);
        }
    }, [alertWvRef]);

    const help = useCallback(() => {
        setShowLoader(false);
        if (alertRef.current) {
            alertRef.current.show({
                type: alertType.message,
                content: contentHelp,
            });
        }
    }, [getLabel])

    useEffect(() => {
        setShowLoader(false);
        if (alertWvRef.current && selectedWord) {
            if (isMobileView) {
                alertWvRef.current.show({
                    type: alertType.message,
                    content: <WordVocalization
                        selectedWord={selectedWord}
                        baseWord={selectedBaseWord}
                        setSelectedWord={setSelectedWord}
                        onWordChange={handleWordChange}
                        closeVwAlert={closeVwAlert}
                    />,
                });
            }
        }
    }, [alertWvRef, selectedWord, selectedBaseWord, setSelectedWord, closeVwAlert, isMobileView, wordPosition]);

    const removeVocalization = useCallback(() => {
        if (!dataText || dataText.length === 0) {
            const source = (userInputRef.current && typeof userInputRef.current.value === 'string' ? userInputRef.current.value : '');
            if (!source || !source.trim()) {
                return;
            }
            setdataText(source);
        }
        setTextRemove(true);
    }, [dataText, userInputRef])

    const checkWord = useCallback((oneWord) => {
        if (!/\s/.test(oneWord) && !/[a-zA-Z]/.test(oneWord)) {
            setSelectedWord(oneWord)
        }
    }, [])

    useEffect(() => {
        if (dataText && dataNakdanRestoreKtivMale?.OutputText && textRemove == true) {
            setdataText(dataNakdanRestoreKtivMale.OutputText)
            setTextRemove(false)
        }
    }, [dataText, textRemove, dataNakdanRestoreKtivMale, userInput, userInputRef]);

    useEffect(() => {
        if (dataText !== null && dataText !== undefined) {
            userInputRef.current.value = dataText
        }
    }, [dataText])


    useEffect(() => {
        if (dataNakdanText?.OutputText) {
            setdataText(dataNakdanText.OutputText);
            setOriginalInputText(dataNakdanText.InputText || lastSubmittedInput || "");
            setTextRemove(false);
            setuserInput("");
            if (userInputRef.current) {
                userInputRef.current.readOnly = true;
            }
        } else if (errorNakdanText) {
            console.log(errorNakdanText);
            if (userInputRef.current) {
                userInputRef.current.readOnly = false;
            }
        } else {
            // No response yet: ensure users can still edit
            if (userInputRef.current) {
                userInputRef.current.readOnly = false;
            }
        }
        setTextRemove(false);
    }, [dataNakdanText, errorNakdanText, lastSubmittedInput]);


    const sanitizeForNakdan = useCallback((value) => {
        if (!value) return value;
        // Remove common bullet-like characters that break the backend
        const BULLETS_REGEX = /[•·∙●◦‣⁃◘◙▪▫◾◼◻■□◆◇◊]/g; // U+2022, U+00B7, U+2219, U+25CF, U+25E6, U+2023, U+2043, etc.
        return value.replace(BULLETS_REGEX, ' ');
    }, []);

    const onVocalizationButtonClickHandle = useCallback(() => {
        const userInputValue = userInputRef.current?.value;
        const currentDisplayed = dataText;
        if (userInputValue != null) {
            // Clean bullet characters before normalization
            const cleanedValue = sanitizeForNakdan(userInputValue);
            const normalizedValue = normalizeTextForSearch(cleanedValue);
            setLastSubmittedInput(normalizedValue);
            // If the displayed data equals the normalized input (no change), force re-trigger
            if (currentDisplayed && currentDisplayed === normalizedValue) {
                setdataText(null);
                setTimeout(() => setuserInput(normalizedValue), 0);
                return;
            }
            // If the request input equals the last request input, force a change to re-trigger the hook
            if (normalizedValue === userInput) {
                setuserInput("");
                setTimeout(() => setuserInput(normalizedValue), 0);
                return;
            }
            setuserInput(normalizedValue);
        }
    }, [dataText, userInput, sanitizeForNakdan]);

    const handleWordClick = useCallback((event) => {
        const textarea = userInputRef.current;
        const textValue = textarea.value;
        const caretPosition = textarea.selectionStart;

        const wordDelimiters = [' ', '\n', '\t', '.', ',', ';', ':', '!', '?'];
        let start = caretPosition;
        while (start > 0 && !wordDelimiters.includes(textValue[start - 1])) {
            start--;
        }
        let end = caretPosition;
        while (end < textValue.length && !wordDelimiters.includes(textValue[end])) {
            end++;
        }
        const word = textValue.substring(start, end);
        textarea.setSelectionRange(start, end);
        const trimmedWord = word.trim();
        const splitRegex = /[\n\s,.]+/;
        const countWordsUntilSelection = (value, selectionEnd) => {
            return value.substring(0, selectionEnd).split(splitRegex).filter(Boolean).length;
        };
        const wordIndex = countWordsUntilSelection(textValue, end) - 1;
        const baseWords = (originalInputText || "").split(splitRegex).filter(Boolean);
        const removeNiqqud = (val) => (val || "").replace(/[\u0591-\u05C7]/g, '');
        const stringSimilarity = (a, b) => {
            if (!a || !b) return 0;
            if (a === b) return 1;
            const makeBigrams = (str) => {
                const s = str.toLowerCase();
                const bigrams = {};
                for (let i = 0; i < s.length - 1; i++) {
                    const bg = s.substring(i, i + 2);
                    bigrams[bg] = (bigrams[bg] || 0) + 1;
                }
                return bigrams;
            };
            const bgA = makeBigrams(a);
            const bgB = makeBigrams(b);
            let intersection = 0;
            let union = 0;
            const keys = new Set([...Object.keys(bgA), ...Object.keys(bgB)]);
            keys.forEach(k => {
                const aCount = bgA[k] || 0;
                const bCount = bgB[k] || 0;
                intersection += Math.min(aCount, bCount);
                union += Math.max(aCount, bCount);
            });
            return union === 0 ? 0 : intersection / union;
        };

        const selectedBase = removeNiqqud(trimmedWord);
        let baseWord = baseWords[wordIndex] || "";

        if (!baseWord && trimmedWord) {
            baseWord = baseWords.find(w => removeNiqqud(w) === selectedBase) || trimmedWord;
        } else if (baseWord) {
            const sim = stringSimilarity(selectedBase, removeNiqqud(baseWord));
            if (sim < 0.65) {
                baseWord = selectedBase || trimmedWord;
            }
        }

        checkWord(trimmedWord);
        setSelectedBaseWord(baseWord || trimmedWord);
        setWordPosition({ start, end });

    }, [setWordPosition, checkWord, userInputRef, wordPosition, originalInputText]);

    const handleWordChange = useCallback((newWord) => {
        const textarea = userInputRef.current;
        const textValue = textarea.value;
        const updatedText = textValue.substring(0, wordPosition.start) + newWord + textValue.substring(wordPosition.end);
        setWordPosition(null)
        setdataText(updatedText);
        setSelectedWord("");
        setSelectedBaseWord("");
    }, [setdataText, setWordPosition, userInputRef, wordPosition]);


    const handleMobileTouchStart = useCallback((event) => {
        const touch = event.touches[0];
        const currentTime = Date.now();
        
        setTouchStartTime(currentTime);
        setTouchStartPos({ x: touch.clientX, y: touch.clientY });
        setIsTouchMoved(false);
    }, []);
    

    const handleMobileTouchMove = useCallback((event) => {
        setIsTouchMoved(true);
    }, []);

    const handleMobileTouchEnd = useCallback((event) => {
        const currentTime = Date.now();
        const touchDuration = currentTime - touchStartTime;
        const timeSinceLastTap = currentTime - lastTapTime;
        
        // If the user was scrolling, don't handle the tap
        if (isTouchMoved) {
            return;
        }
    
        const touch = event.changedTouches[0];
        const clickEvent = {
            clientX: touch.clientX,
            clientY: touch.clientY,
            target: event.target
        };
    
        // Check for double tap
        if (timeSinceLastTap < DOUBLE_TAP_THRESHOLD) {
            handleWordClick(clickEvent);
            setLastTapTime(0); // Reset last tap time to prevent triple-tap
        }
        // Check for long press
        else if (touchDuration >= LONG_PRESS_DURATION) {
            handleWordClick(clickEvent);
        }
        // Update last tap time for potential double tap
        else {
            setLastTapTime(currentTime);
        }
    }, [handleWordClick, touchStartTime, isTouchMoved, lastTapTime]);

    const onTextareaInputChange = useCallback(() => {
        // Allow in-place edits after vocalization while keeping current text and base-word mapping
        if (userInputRef.current) {
            setdataText(userInputRef.current.value || "");
            userInputRef.current.readOnly = false;
        } else {
            setdataText("");
        }
        setSelectedWord("");
        setSelectedBaseWord("");
        setTextRemove(false);
    }, []);

    const nav = useMemo(() => {
        return <nav className={styles.nav} role="toolbar" aria-label="סרגל כלים">
            <button type="button" onClick={removeVocalization} aria-label={getLabel("nakdan_Remove")}>
                <span>{getLabel("nakdan_Remove")}</span>
                <Icon icon="remove_text" className={styles.one_svg} width="25" height="25" />
            </button>
            <button type="button" onClick={increaseFontSize} aria-label={getLabel("nakdan_increase")}>
                <span>{getLabel("nakdan_increase")}</span>
                <div className={styles.tow_svg}>
                    <Icon icon="letter" width="13" height="15" />
                    <Icon icon="plus" width="13" height="11" />
                </div>
            </button>
            <button type="button" onClick={decreaseFontSize} aria-label={getLabel("nakdan_decrease")}>
                <span>{getLabel("nakdan_decrease")}</span>
                <div className={styles.tow_svg}>
                    <Icon icon="letter" width="13" height="15" />
                    <Icon icon="minus" width="13" height="13" />
                </div>
            </button>
            <button type="button" onClick={clearText} aria-label={getLabel("nakdan_clear_sceen")}>
                <span>{getLabel("nakdan_clear_sceen")}</span>
                <Icon icon="clear" className={styles.one_svg} width="25" height="25" />
            </button>
            <button type="button" onClick={help} aria-label={getLabel("nakdan_help")}>
                <span>{getLabel("nakdan_help")}</span>
                <Icon icon="help" className={styles.one_svg} width="25" height="25" />
            </button>
        </nav>;
    }, [getLabel, increaseFontSize, decreaseFontSize, clearText]);

    const contentHelp = useMemo(() => {
        return <div className={styles.help_pop_up}>
            <div className={styles.block_explain}>
                <span className={styles.header}>{getLabel("nakdan_header1")}</span>
                <ul>
                    <li><span>{getLabel("nakdan_Vocalization")}</span>{getLabel("nakdan_Vocalizatio_content")}</li>
                    <li><span>{getLabel("nakdan_Remove_Vocalization")}</span>{getLabel("nakdan_Remove_Vocalization_content")}</li>
                    <li><span>{getLabel("nakdan_increase")}</span> {getLabel("nakdan_Increase_Text_Size_content")}</li>
                    <li><span>{getLabel("nakdan_decrease")}</span>{getLabel("nakdan_Decrease_Text_Size_content")}</li>
                    <li><span>{getLabel("nakdan_clear_screen")}</span>{getLabel("nakdan_clear_screen_content")}</li>
                </ul>
            </div>
            <div className={styles.block_explain}>
                <span className={styles.header}>{getLabel("nakdan_header2")}</span>
                {!isMobileView &&
                    <div>{getLabel("nakdan_mid_text")}</div>
                }
                {isMobileView &&
                    <div>{getLabel("nakdan_mid_text_phone")}</div>}
                <ul>
                    {isMobileView &&
                        <>
                            <li><span>{getLabel("nakden_choose_word")}</span>{getLabel("nakden_choose_word_contnt_phone")}</li>
                            <li><span>{getLabel("nakdan_Manual_Vocalization")}</span>{getLabel("nakdan_Manual_Vocalization_content_phone")}</li>
                        </>}
                    {!isMobileView && <>
                        <li><span>{getLabel("nakden_choose_word")}</span>{getLabel("nakden_choose_word_contnt")}</li>
                        <li><span>{getLabel("nakdan_Manual_Vocalization")}</span>{getLabel("nakdan_Manual_Vocalization_content")}</li>
                        <li><span>{getLabel("nakdan_cancel")}</span> {getLabel("nakdan_close_content")}</li></>}
                </ul>
            </div>
            <Button
                type={buttonType.premium}
                className={styles.close_button}
                onClick={closeAlert}
            >
                {getLabel("nakdan_help_alert_button")}
            </Button>
        </div>
    }, [closeAlert, getLabel, isMobileView])

    const showPanel = dataText && !!selectedWord;

    return (
        <>
            <OuterLayer container={appLayoutTitleContainer} TagWrapper='span'>{getLabel("page_nakdan_title")}</OuterLayer>
            {!isMobileView &&
                <>
                    <OuterLayer container={appLayoutSubTitleContainer} dangerouslySetInnerHTML={{ __html: getLabel("page_nakdan_sub_title") }} />
                    <div className={styles.container}>
                        <div className={styles.content}>
                            {nav}
                            <div className={`${styles.text_row} ${showPanel ? styles.text_row_split : ''}`}>
                                <div className={styles.text_box}>
                                    <textarea
                                        id="Textarea"
                                        className={styles.user_text}
                                        ref={userInputRef}
                                        onDoubleClick={handleWordClick}
                                        onInput={onTextareaInputChange}
                                        style={{ fontSize: `${fontSize}rem` }}
                                        disabled={false}
                                        autoFocus={true}
                                    />
                                </div>
                                {showPanel && <WordVocalization selectedWord={selectedWord} baseWord={selectedBaseWord} setSelectedWord={setSelectedWord} onWordChange={handleWordChange} />}
                            </div>
                            {!dataText && (
                                <Button className={styles.dark_botton_submit} onClick={onVocalizationButtonClickHandle}>
                                    {getLabel('nakdan_submit')}
                                </Button>
                            )}
                            {dataText && <>
                                <div className={styles.instructions}><span>{getLabel("nakdan_instructions")}</span></div>
                                <Button className={styles.dark_botton_submit} onClick={onVocalizationButtonClickHandle}>
                                    {getLabel('nakdan_submit')}
                                </Button>
                            </>}
                        </div>
                    </div>
                </>
            }
            {isMobileView &&
                <div className={styles.container} ref={containerRef}>
                    <div className={styles.content}>
                        {dataText && nav}
                        <div className={styles.text_box}>
                            <textarea
                                id="Textarea"
                                className={styles.user_text}
                                ref={userInputRef}
                                onTouchStart={handleMobileTouchStart}
                                onTouchMove={handleMobileTouchMove}
                                onTouchEnd={handleMobileTouchEnd}
                                onInput={onTextareaInputChange}
                                style={{ fontSize: `${fontSize}rem` }}
                                disabled={!!selectedWord}
                                autoFocus={false}
                            />
                        </div>
                        {dataText && <>
                            {!selectedWord && < div className={styles.instructions}><span>{getLabel("nakdan_instructions_phone")}</span></div>}
                            <Alert ref={alertWvRef} lightMask={true} showClose={false} />
                            {showLoader && <Loader />}
                        </>
                        }
                        <Button className={styles.dark_botton_submit} onClick={onVocalizationButtonClickHandle}>
                            {getLabel('nakdan_submit')}
                        </Button>
                    </div>
                </div >
            }
            <Alert ref={alertRef} />
            {showLoader && <Loader />}

        </>
    );
};

export default Nakdan;